import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { submitPublicContactEnquiry } from "./publicContactEnquiryApi";
import {
  PUBLIC_BOOKING_PHONE_COUNTRIES,
  normalizePublicBookingNationalNumber,
  resolvePublicBookingPhoneCountryCode,
} from "./publicBookingPhone";

const EMPTY_VALUES = Object.freeze({
  name: "",
  phone: "",
  phoneCountryCode: "",
  email: "",
  message: "",
  preference: "",
  company: "",
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+0-9][0-9\s().-]{5,38}$/;

function clean(value) {
  return String(value == null ? "" : value).trim();
}

const getPublicContactPhoneCountry = (countryCode) =>
  PUBLIC_BOOKING_PHONE_COUNTRIES.find(
    (country) => country.code === String(countryCode || "").trim(),
  ) || null;

export function buildPublicContactPhoneValue(values) {
  const rawPhone = clean(values?.phone);
  const countryCode = clean(values?.phoneCountryCode);

  if (!rawPhone) return "";
  if (!countryCode) return rawPhone;

  const country = getPublicContactPhoneCountry(countryCode);
  const nationalNumber = normalizePublicBookingNationalNumber(
    rawPhone,
    countryCode,
  );

  if (!country?.dialCode || !nationalNumber) return rawPhone;

  return `${country.dialCode}${nationalNumber}`;
}

export function validatePublicContactValues(values) {
  const name = clean(values?.name);
  const phone = buildPublicContactPhoneValue(values);
  const email = clean(values?.email);
  const message = clean(values?.message);
  const preference = clean(values?.preference);

  if (name.length < 2) return "Please enter your full name.";
  if (name.length > 100) return "Name must be 100 characters or fewer.";
  if (!phone && !email) return "Please enter a phone number or email address.";
  const phoneDigitCount = phone.replace(/\D/g, "").length;
  if (
    phone &&
    (!PHONE_PATTERN.test(phone) || phoneDigitCount < 7 || phoneDigitCount > 15)
  ) {
    return "Please enter a valid phone number.";
  }
  if (email && (!EMAIL_PATTERN.test(email) || email.length > 254)) {
    return "Please enter a valid email address.";
  }
  if (message.length < 5) return "Please enter a little more detail in your message.";
  if (message.length > 1200) return "Message must be 1200 characters or fewer.";
  if (preference.length > 160) return "Preference must be 160 characters or fewer.";

  return "";
}

export default function usePublicContactEnquiry({
  venue,
  previewMode = false,
  initialValues,
  successMessage = "Thanks! Your message has been sent.",
} = {}) {
  const defaultPhoneCountryCode = resolvePublicBookingPhoneCountryCode(venue);

  const [values, setValues] = useState(() => ({
    ...EMPTY_VALUES,
    phoneCountryCode: defaultPhoneCountryCode,
    ...(initialValues || {}),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const venueSlug = clean(venue?.slug);

  const setField = useCallback((name, value) => {
    if (!Object.prototype.hasOwnProperty.call(EMPTY_VALUES, name)) return;
    setValues((previous) => ({ ...previous, [name]: value }));
    setFeedback(null);
  }, []);

  const handleChange = useCallback(
    (event) => {
      setField(event?.target?.name, event?.target?.value ?? "");
    },
    [setField],
  );

  const reset = useCallback(() => {
    setValues({
      ...EMPTY_VALUES,
      phoneCountryCode: defaultPhoneCountryCode,
    });
    setFeedback(null);
  }, [defaultPhoneCountryCode]);

  const payload = useMemo(
    () => ({
      name: clean(values.name),
      phone: buildPublicContactPhoneValue(values),
      email: clean(values.email).toLowerCase(),
      message: clean(values.message),
      preference: clean(values.preference),
      company: clean(values.company),
    }),
    [values],
  );

  const submit = useCallback(
    async (event) => {
      event?.preventDefault?.();

      if (previewMode || submitting) return false;

      if (!venueSlug) {
        const message = "This venue page is missing its public website identifier.";
        setFeedback({ type: "error", message });
        toast.error(message);
        return false;
      }

      const validationError = validatePublicContactValues(payload);
      if (validationError) {
        setFeedback({ type: "error", message: validationError });
        toast.error(validationError);
        return false;
      }

      setSubmitting(true);
      setFeedback(null);

      try {
        await submitPublicContactEnquiry(venueSlug, payload);
        setValues({
          ...EMPTY_VALUES,
          phoneCountryCode: defaultPhoneCountryCode,
        });
        setFeedback({ type: "success", message: successMessage });
        toast.success(successMessage);
        return true;
      } catch (error) {
        const message =
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.message ||
          "Unable to send your message right now. Please try again.";
        setFeedback({ type: "error", message });
        toast.error(message);
        return false;
      } finally {
        setSubmitting(false);
      }
    }, [
      defaultPhoneCountryCode,
      payload,
      previewMode,
      submitting,
      successMessage,
      venueSlug,
    ],
  );

  return {
    values,
    setField,
    handleChange,
    submit,
    submitting,
    feedback,
    reset,
  };
}
